import React, { FC, useState } from 'react';
import { Box } from '@mui/system';
import { useTranslation } from 'react-i18next';
import CountryInput from 'components/shared/country-input/country-input.component';
import { Button, FormControl } from '@mui/material';
import OrganizationNumberInput from 'components/shared/organization-number/organization-number.component';
import { Country } from 'models/country';
import { useFormik } from 'formik';

type OrganizationLookupProps = {
    onOrganizationSelected: (selectOrganizationForm: OrganizationLookupFormData) => void;
    onOrganizationChanged?: (selectOrganizationForm: OrganizationLookupFormData) => void;
    onClose: () => void;
    country?: Country;
    allowManualAdd?: boolean;
    onManualAddClicked?: () => void;
    manualTitle?: string;
};
const OrganizationLookup: FC<OrganizationLookupProps> = ({
    onOrganizationSelected,
    onClose,
    onOrganizationChanged,
    country,
    allowManualAdd,
    onManualAddClicked,
    manualTitle,
}: OrganizationLookupProps) => {
    const { t: translate } = useTranslation(['common', 'organization', 'client-directory', 'organization-numbers']);
    const [isOrganizationNumberValid, setIsOrganizationNumberValid] = useState(true);
    const [currentCountry, setCurrentCountry] = useState((country || {}) as Country);
    const validate = (values: OrganizationLookupFormData) => {
        const errors: Record<string, string> = {};
        if (!values.organizationNumber) {
            errors.organizationNumber = translate('organization:VALIDATION:ORGANIZATION_NUMBER');
        }
        if (!values.countryCode) {
            errors.countryCode = translate('organization:VALIDATION:COUNTRY_CODE');
        }
        if (values.organizationNumber && !isOrganizationNumberValid) {
            errors.organizationNumber = translate(
                `organization-numbers:${currentCountry.alpha2Code}_INVALID_ORGANIZATION_NUMBER`,
            );
        }
        return errors;
    };

    const formik = useFormik<OrganizationLookupFormData>({
        initialValues: {
            organizationNumber: '',
            countryCode: country?.alpha2Code || '',
        },
        validate,
        validateOnMount: true,
        enableReinitialize: true,
        onSubmit: (form: OrganizationLookupFormData) => {
            onOrganizationSelected(form);
        },
    });

    const handleCountrySelect = (country: Country) => {
        formik.setFieldValue('countryCode', country.alpha2Code);
        setCurrentCountry(country);
        setIsOrganizationNumberValid(false);
        formik.setFieldValue('organizationNumber', '');
        if (onOrganizationChanged) {
            onOrganizationChanged(formik.values);
        }
    };

    const handleOrganizationNumberChange = (value: string, isOkay: boolean) => {
        formik.setFieldValue('organizationNumber', value);
        setIsOrganizationNumberValid(isOkay);
        setTimeout(() => {
            formik.validateForm();
        }, 0);
        if (onOrganizationChanged) {
            onOrganizationChanged(formik.values);
        }
    };

    return (
        <form onSubmit={formik.handleSubmit}>
            <Box mt={1}>
                <CountryInput
                    country={currentCountry}
                    onCountrySelect={handleCountrySelect}
                    helperText={formik.touched.countryCode && formik.errors.countryCode}
                    error={formik.touched.countryCode && Boolean(formik.errors.countryCode)}
                    {...formik.getFieldProps('countryCode')}
                />
                {currentCountry.alpha2Code ? (
                    <FormControl margin="dense" fullWidth>
                        <OrganizationNumberInput
                            label={translate(
                                `organization-numbers:${currentCountry.alpha2Code || 'SE'}_ORGANIZATION_NUMBER`,
                            )}
                            required
                            country={currentCountry}
                            disabled={!currentCountry.alpha2Code}
                            margin="dense"
                            onValueChange={handleOrganizationNumberChange}
                            helperText={formik.touched.organizationNumber && formik.errors.organizationNumber}
                            error={formik.touched.organizationNumber && Boolean(formik.errors.organizationNumber)}
                            onBlur={formik.getFieldProps('organizationNumber').onBlur}
                            name={formik.getFieldProps('organizationNumber').name}
                            value={formik.getFieldProps('organizationNumber').value}
                        />
                    </FormControl>
                ) : undefined}
                <Box mt={1} display="flex">
                    <Box flexGrow={1}>
                        <Button onClick={() => onClose()}>{translate('common:CANCEL')}</Button>
                    </Box>
                    {allowManualAdd && manualTitle ? (
                        <Box mr={1}>
                            <Button
                                onClick={() => {
                                    if (onManualAddClicked) {
                                        onManualAddClicked();
                                    }
                                }}
                            >
                                {manualTitle}
                            </Button>
                        </Box>
                    ) : undefined}
                    <Button variant="contained" type="submit" disabled={!formik.isValid}>
                        {translate('common:NEXT_STEP')}
                    </Button>
                </Box>
            </Box>
        </form>
    );
};

export type OrganizationLookupFormData = {
    organizationNumber: string;
    countryCode: string;
};

export default OrganizationLookup;
